/**
 * footfalls.ts — whose step is heard, on what, and how loud (2026-09-20). PURE.
 *
 * The strides already exist: FootprintDecalModule walks a stepper for every
 * other traveler and every outdoor versona within 30 m to lay their prints.
 * Each stride is also a SOUND — synthesised (synth.ts `synthFootstep`), placed
 * where the foot landed — and this file holds the three decisions around it:
 * the surface, the level, and the budget (a plaza of fifty walkers is a hundred
 * footfalls a second; the ear wants the nearest few).
 *
 * The player's own steps are NOT here: they stay the recorded loops the
 * ambience module plays (tuned by ear, and heard at arm's length where a model
 * would be judged hardest). ONE biome table serves both, so a versona beside
 * you never walks on a different ground than you do.
 */
import type { Biome } from '@shared/terrain.js';
import type { FootSurface } from './synth.js';
import { inverseDistanceGain } from './spatial_math.js';

/** What each biome's ground is underfoot. The recorded loops are `steps_<surface>`; the synthesised steps `foot_<surface>`. */
export const BIOME_FOOT_SURFACE: Record<Biome, FootSurface> = {
  regolith_plains: 'regolith',
  rocky_highlands: 'regolith',
  dune_desert: 'sand',
  moss_wetland: 'mud',
  fungal_basin: 'mud',
  crystal_forest: 'grass',
};

/** Snow lies over paving and moss alike; a town paves over its biome; otherwise the biome speaks. */
export function footSurfaceFor(o: { snow: boolean; paved: boolean; biome: Biome | null }): FootSurface {
  if (o.snow) return 'snow';
  if (o.paved) return 'stone';
  return o.biome ? BIOME_FOOT_SURFACE[o.biome] : 'grass';
}

export type Walker = 'traveler' | 'versona';
/** Past this nobody's step is asked for at all (a traveler matters more than one face in a crowd). */
export const FOOTFALL_RANGE_M: Record<Walker, number> = { traveler: 28, versona: 18 };
const FOOTFALL_BASE: Record<Walker, number> = { traveler: 0.3, versona: 0.22 };
/** Full level inside this (m) — then 1/d, eased to nothing at the range. */
export const FOOTFALL_REF_M = 2.5;

/** The CALLER's distance gain (the panner adds direction only — one distance owner). A run lands harder. */
export function footfallGain(distM: number, who: Walker, running: boolean): number {
  return FOOTFALL_BASE[who] * (running ? 1.3 : 1) * inverseDistanceGain(distM, FOOTFALL_REF_M, FOOTFALL_RANGE_M[who]);
}

// ── cadence: how OFTEN a foot lands ──

/**
 * Steps per second for a ground speed. NOT the decal stepper's stride: Atlas moves at game speeds (walk 6 m/s, sprint
 * 9) and a print every 1.05 m is right for the EYE but 5.7 footfalls a second is a machine-gun to the ear. Human
 * cadence saturates — a stroll is ~1.9 Hz, a flat-out run ~3 — so the sound follows a saturating curve of speed:
 * 1.5 m/s → 1.99 · 3 → 2.28 · 6 → 2.65 · 9 → 2.85 Hz. Faster still FOLLOWS PACE; it just never rattles.
 */
export function stepCadenceHz(speedMps: number): number {
  return 1.6 + 1.5 * (1 - Math.exp(-Math.max(0, speedMps) / 5));
}

/** Distance-driven: no movement, no steps; a teleport lays none; a long frame lays at most two. */
export class StepClock {
  private acc = 0;
  /** Footfalls due after moving `distM` at `speedMps`. */
  advance(distM: number, speedMps: number): number {
    if (!(distM > 0) || !(speedMps > 0.2)) return 0;      // standing, or a shuffle on the spot
    if (distM > 6) { this.acc = 0; return 0; }            // a teleport is not a stride
    const stride = speedMps / stepCadenceHz(speedMps);
    this.acc += distM;
    let n = 0;
    while (this.acc >= stride && n < 2) { this.acc -= stride; n++; }
    if (this.acc > stride) this.acc = stride * 0.5;       // a hitch must not bank a burst
    return n;
  }
  reset(): void { this.acc = 0; }
}

// ── the player's own steps ──

/**
 * Written by AtlasAmbienceModule (which owns "may my feet be heard?" and "on what?"), read by FootprintDecalModule
 * (which sees every frame of movement). `contact` = recorded takes exist for this ground, so the steps are
 * CONTACT-DRIVEN one-shots that follow pace; otherwise the ambience module keeps playing its recorded loop.
 */
export interface PlayerSteps { onFoot: boolean; surface: FootSurface | 'water'; contact: boolean; played: number }
const stepHolder = globalThis as unknown as { __atlasPlayerSteps?: PlayerSteps };
export function playerSteps(): PlayerSteps { return (stepHolder.__atlasPlayerSteps ??= { onFoot: false, surface: 'grass', contact: false, played: 0 }); }

/** Your own footfall, head-locked: a walk 0.4, a sprint 0.55; a crouch is a third of that (the whole point of crouching). */
export function playerFootfallGain(speedMps: number, crouched: boolean): number {
  const run = Math.min(1, Math.max(0, (speedMps - 5) / 4));
  return (0.4 + 0.15 * run) * (crouched ? 0.35 : 1);
}

/**
 * A token bucket that PREFERS THE NEAR: a step costs half a token at your elbow and one and a half at the edge of
 * earshot, so under a crowd the far steps are the ones that go unsaid. (Sorting every walker by distance each frame
 * would do it exactly; this does it well enough for nothing.)
 */
export class FootfallBudget {
  private tokens: number;
  constructor(readonly perSecond = 9, readonly burst = 5) { this.tokens = burst; }
  tick(dt: number): void { this.tokens = Math.min(this.burst, this.tokens + this.perSecond * Math.min(0.25, Math.max(0, dt))); }
  /** `nearness` 0 (at the listener) … 1 (at the range). */
  take(nearness: number): boolean {
    const cost = 0.5 + Math.min(1, Math.max(0, nearness));
    if (this.tokens < cost) return false;
    this.tokens -= cost;
    return true;
  }
  get level(): number { return this.tokens; }
}
